import {
  type Appointment,
  type AppointmentReasonCode,
  type AppointmentStatus,
  type Task,
  type hfb,
  useBokaStoresAndServices,
} from "@repo/api";
import { checkmarkIcon, crossIcon } from "@repo/skapa";
import { type HfbConfig, type HfbConfiguration, useSettings } from "@repo/ui";
import { hasItems, isDefined } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";

import {
  type CoWorkerScheduleActivity,
  type OutlookEvent,
} from "../calendar/types";

export const isAssociatedWithProject = (opportunityId: number | undefined) =>
  opportunityId !== 0 && opportunityId !== undefined;

export const getQuestionnaires = (appointments: Appointment[]) => {
  return appointments
    .filter(
      (appointment): appointment is Appointment & { appointmentId: number } =>
        appointment.appointmentId !== undefined
    )
    .sort(
      (a, b) =>
        new Date(b.bookingTime ?? b.timeWindowStartTime).getTime() -
        new Date(a.bookingTime ?? a.timeWindowStartTime).getTime()
    )
    .map((appointment) => ({
      id: appointment.appointmentId,
      startDate: appointment.timeWindowStartTime,
      endDate: appointment.timeWindowEndTime,
      questions: appointment.additionalInformation,
      status: appointment.appointmentStatus,
      service: appointment.service,
    }));
};

const completedStatus = ["COMPLETED"];
const cancelledStatus = ["CANCELLED_NO_SHOW", "CANCELLED"];
export const inactiveStatus = ["COMPLETED", "CANCELLED_NO_SHOW", "CANCELLED"];

export const isAppointment = (
  data: Appointment | Task | OutlookEvent | CoWorkerScheduleActivity
): data is Appointment => {
  return (
    (data as Appointment)?.appointmentType === "GENERAL_BOOKING" ||
    (data as Appointment)?.appointmentType === "INTERNAL_BOOKING" ||
    (data as Appointment)?.appointmentType === "WALKIN_BOOKING" ||
    (data as Appointment)?.appointmentType === "DROP_IN"
  );
};

export const isCompletedStatus = (status?: AppointmentStatus): boolean =>
  status !== undefined && completedStatus.includes(status);

export const isOpenSlot = (
  reasonCode?: AppointmentReasonCode,
  orderNumber?: string | null,
  type?: string
): boolean =>
  type !== "DROP_IN" &&
  (reasonCode === "BOKA_OPEN_SLOT" || isEmpty(orderNumber));

export const isCancelledStatus = (
  appointmentStatus?: AppointmentStatus
): boolean =>
  appointmentStatus !== undefined &&
  cancelledStatus.includes(appointmentStatus);

export const isInactiveStatus = (
  appointmentStatus?: AppointmentStatus
): boolean =>
  appointmentStatus !== undefined && inactiveStatus.includes(appointmentStatus);

export const getAppointmentStatusIcon = (status?: AppointmentStatus) => {
  if (isCancelledStatus(status)) {
    return crossIcon;
  } else {
    return checkmarkIcon;
  }
};

const findHfbForServiceArea = (
  serviceArea: string,
  hfbList?: HfbConfiguration,
  hfbConfig?: Partial<Record<hfb, HfbConfig>>
) => {
  const sa = hfbList?.hfb_list?.find((el) =>
    el.service_areas?.some((area) => area.name_key === serviceArea)
  );

  const match = sa?.hfb_code_key ?? sa?.hfb_code_key;

  if (match) {
    return match;
  }

  // if no hfb is found, use default hfb97
  return (Object.entries(hfbConfig ?? {}).find(([, value]) =>
    value.service_areas?.map((el) => el.name_key).includes(serviceArea)
  )?.[0] ?? "hfb97") as hfb;
};

const findHfbForService = (
  service: string,
  hfbList?: HfbConfiguration,
  hfbConfig?: Partial<Record<hfb, HfbConfig>>
) => {
  const sa = hfbList?.hfb_list?.find((el) =>
    el.service_areas?.some((area) =>
      area.services?.some((s) => s.name_key === service)
    )
  );

  const match = sa?.hfb_code_key ?? sa?.hfb_code_key;

  if (match) {
    return match;
  }

  // if no hfb is found, use default hfb97
  return (Object.entries(hfbConfig ?? {}).find(([, value]) =>
    value.service_areas
      ?.flatMap((el) => el.services)
      .some((s) => s?.name_key === service)
  )?.[0] ?? "hfb97") as hfb;
};

const mapServiceAreasToHfb = (
  areas: string[],
  hfbConfig?: Partial<Record<hfb, HfbConfig>>,
  hfbList?: HfbConfiguration
): hfb[] => {
  const hfbs = areas?.reduce((acc, area) => {
    const theHfb = findHfbForServiceArea(area, hfbList, hfbConfig);
    return [...acc, theHfb];
  }, [] as hfb[]);
  return uniq(hfbs);
};

const getHfbServiceMap = (
  services: string[],
  hfbConfig?: Partial<Record<hfb, HfbConfig>>,
  hfbList?: HfbConfiguration
): Record<hfb, string[]> => {
  const hfbServices = services.reduce(
    (acc, service) => {
      const theHfb = findHfbForService(service, hfbList, hfbConfig);
      const serviceList = [...(acc[theHfb] ?? []), service];
      return { ...acc, [theHfb]: uniq(serviceList) };
    },
    {} as Record<hfb, string[]>
  );

  return hfbServices;
};

export const useAppointmentConfig = (cmpCode?: string, hfbCode?: hfb) => {
  const { boka_config, market } = useSettings();
  const { data: bokaStoresAndServices } = useBokaStoresAndServices();
  const enableAll = boka_config?.enable_all_stores_and_services;

  const allStores = enableAll
    ? boka_config?.stores
    : boka_config?.stores?.filter((el) => el.enable);

  const selectedStore = allStores?.find(
    (store) => store.unit_id_key === cmpCode
  );
  let hfbOptions: hfb[], serviceOptions;

  // is enable all, services and hfb are taken from Appointment Config API (Prod) or HFB config from Config Tool(Dev/Stage)
  if (enableAll) {
    hfbOptions =
      cmpCode && selectedStore?.name
        ? !isEmpty(bokaStoresAndServices) // check if we get config from Appt Config API (Prod)
          ? mapServiceAreasToHfb(
              Object.keys(bokaStoresAndServices[selectedStore?.name] ?? {}),
              boka_config?.hfb,
              boka_config?.hfb_configuration
            )
          : ((boka_config?.hfb_configuration?.hfb_list?.map(
              (h) => h.hfb_code_key ?? h.hfb_code_key
            ) as hfb[]) ?? (Object.keys(boka_config?.hfb ?? {}) as hfb[]))
        : [];

    // Hack since BE sends REMOTE-AU instead of Remote Customer Meeting Point
    if (
      !hasItems(hfbOptions) &&
      selectedStore?.name?.toLowerCase().includes("remote")
    ) {
      hfbOptions =
        cmpCode && selectedStore?.name
          ? !isEmpty(bokaStoresAndServices) // check if we get config from Appt Config API (Prod)
            ? mapServiceAreasToHfb(
                Object.keys(
                  bokaStoresAndServices[`REMOTE-${market?.toUpperCase()}`] ?? {}
                ),
                boka_config?.hfb,
                boka_config?.hfb_configuration
              )
            : ((boka_config?.hfb_configuration?.hfb_list?.map(
                (h) => h.hfb_code_key
              ) as hfb[]) ?? (Object.keys(boka_config?.hfb ?? {}) as hfb[]))
          : [];
    }

    const allServiceAreas = hfbCode
      ? (boka_config?.hfb_configuration?.hfb_list?.find(
          (h) => h.hfb_code_key === hfbCode
        )?.service_areas ?? boka_config?.hfb?.[hfbCode]?.service_areas)
      : [];

    if (selectedStore?.name) {
      serviceOptions = allServiceAreas
        ?.map((area) =>
          !isEmpty(bokaStoresAndServices)
            ? bokaStoresAndServices[selectedStore?.name ?? ""]?.[area.name_key]
            : area.services?.map((s) => s.name_key)
        )
        .filter(isDefined);

      // Hack since BE sends REMOTE-AU instead of Remote Customer Meeting Point
      if (
        !hasItems(serviceOptions) &&
        selectedStore?.name?.toLowerCase().includes("remote")
      ) {
        serviceOptions = allServiceAreas?.map((area) =>
          !isEmpty(bokaStoresAndServices)
            ? bokaStoresAndServices[`REMOTE-${market?.toUpperCase()}`]?.[
                area.name_key
              ]
            : area.services?.map((s) => s.name_key)
        );
      }
    }

    serviceOptions = serviceOptions?.flat(2).filter(isDefined) ?? [];
  } else {
    const allServices =
      selectedStore?.service_configs?.map((s) => s.service_name_key) ?? [];
    const hfbServicesMap = getHfbServiceMap(
      allServices,
      boka_config?.hfb,
      boka_config?.hfb_configuration
    );

    hfbOptions = Object.keys(hfbServicesMap) as hfb[];
    serviceOptions = hfbCode ? hfbServicesMap[hfbCode] : [];
  }

  return {
    cmps: allStores,
    hfbs: hfbOptions,
    services: uniq(serviceOptions),
  };
};
